import { useState } from "react";

import {
  useAtualizarUsuario,
  useCriarUsuario,
  useUsuarios,
} from "../../lib/cadastros/hooks";
import type { UsuarioOut, UsuarioPatch } from "../../lib/cadastros/tipos";
import { useTemPermissaoIrrestrita } from "../../lib/auth/permissoes";
import { useSessaoStore } from "../../lib/auth/store";
import { ErroHttp } from "../../lib/http/erros";
import {
  FormularioUsuario,
  type DadosUsuarioForm,
} from "../cadastros/FormularioUsuario";

/*
 * Página de Usuários do módulo de Cadastros (FE-4 CP-C).
 *
 * Ação `gerenciar_usuarios` é GLOBAL — o backend exige escopo global. Gate por
 * `useTemPermissaoIrrestrita("gerenciar_usuarios")` (a aba já é gated no layout).
 *
 * SENHA WRITE-ONLY (regra dura): nunca exibida na lista (o `UsuarioOut` nem
 * carrega senha) e nunca pré-preenchida no form. Na edição, só montamos `senha` no
 * corpo do PATCH quando o operador digitou uma nova (`senhaPreenchida`) — string
 * vazia NUNCA vai no corpo (evita gravar credencial em branco).
 *
 * ANTI AUTO-LOCKOUT: o backend devolve 409 se o operador desativar o PRÓPRIO
 * usuário. A UI passa `ehProprioUsuario` ao form (comparando o id do usuário em
 * edição com o `usuario.id` do `useSessaoStore`), que trava o toggle "ativo"; o
 * 409 segue tratado no form como defesa-em-profundidade.
 *
 * Usuários NÃO têm delete: desativar = PATCH `ativo=false` (via toggle).
 *
 * Estado de TELA (`useState`, não Zustand): qual formulário está aberto.
 * SÓ classes Tailwind dos tokens CPMH.
 */

const ACAO_GERENCIAR_USUARIOS = "gerenciar_usuarios";

/** Qual formulário está aberto na tela. `null` = nenhum. */
type FormAberto =
  | { tipo: "criar" }
  | { tipo: "editar"; usuario: UsuarioOut }
  | null;

export function PaginaUsuarios(): JSX.Element {
  const consulta = useUsuarios();
  const criar = useCriarUsuario();
  const atualizar = useAtualizarUsuario();
  const podeGerenciar = useTemPermissaoIrrestrita(ACAO_GERENCIAR_USUARIOS);
  // Id do operador autenticado: base do anti auto-lockout (comparação na edição).
  const meuId = useSessaoStore((estado) => estado.usuario?.id ?? null);

  const [form, setForm] = useState<FormAberto>(null);

  function salvarCriacao(dados: DadosUsuarioForm): void {
    criar.mutate(
      {
        email: dados.email,
        nome: dados.nome,
        senha: dados.senha,
        ativo: dados.ativo,
      },
      { onSuccess: () => setForm(null) },
    );
  }

  function salvarEdicao(usuario: UsuarioOut, dados: DadosUsuarioForm): void {
    // SENHA WRITE-ONLY: só inclui `senha` no corpo se o operador digitou uma nova.
    // String vazia jamais vai no PATCH (vazio = manter a credencial atual).
    const corpo: UsuarioPatch = {
      nome: dados.nome,
      ativo: dados.ativo,
    };
    if (dados.senhaPreenchida) {
      corpo.senha = dados.senha;
    }
    // email NÃO entra no PATCH (identidade de login, não editável).
    atualizar.mutate(
      { id: usuario.id, corpo },
      { onSuccess: () => setForm(null) },
    );
  }

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando usuários…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar os usuários.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const usuarios = consulta.data;

  return (
    <div className="flex flex-col gap-4">
      {podeGerenciar && (
        <div>
          <button
            type="button"
            onClick={() => setForm({ tipo: "criar" })}
            className="rounded-md border border-flame bg-flame px-4 py-1 font-subtitulo text-snow"
          >
            Novo usuário
          </button>
        </div>
      )}

      {usuarios.length === 0 ? (
        <p className="font-corpo text-base text-off-black">
          Nenhum usuário cadastrado.
        </p>
      ) : (
        <table className="w-full border-collapse font-corpo text-base text-off-black">
          <thead>
            <tr className="border-b border-platinum text-left">
              <th className="py-2 pr-4 font-subtitulo">E-mail</th>
              <th className="py-2 pr-4 font-subtitulo">Nome</th>
              <th className="py-2 pr-4 font-subtitulo">Ativo</th>
              <th className="py-2 pr-4 font-subtitulo">Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.map((usuario) => (
              <tr key={usuario.id} className="border-b border-platinum">
                <td className="py-2 pr-4">{usuario.email}</td>
                <td className="py-2 pr-4">{usuario.nome}</td>
                <td className="py-2 pr-4">{usuario.ativo ? "Sim" : "Não"}</td>
                <td className="py-2 pr-4">
                  {podeGerenciar && (
                    <button
                      type="button"
                      onClick={() => setForm({ tipo: "editar", usuario })}
                      className="text-flame hover:underline"
                    >
                      Editar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {form?.tipo === "criar" && (
        <FormularioUsuario
          aoSalvar={salvarCriacao}
          aoCancelar={() => setForm(null)}
          salvando={criar.isPending}
          erro={criar.error}
        />
      )}

      {form?.tipo === "editar" && (
        <FormularioUsuario
          usuario={form.usuario}
          ehProprioUsuario={meuId !== null && form.usuario.id === meuId}
          aoSalvar={(dados) => salvarEdicao(form.usuario, dados)}
          aoCancelar={() => setForm(null)}
          salvando={atualizar.isPending}
          erro={atualizar.error}
        />
      )}
    </div>
  );
}
